import { useCallback, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
	cancelDsChatAction,
	DsChatActionEntry,
	DsChatActionPayload,
	enqueueDsChatAction,
} from 'redux/mode/chatActionQueue';
import { RootReducer } from 'redux/reducers';
import { v4 as uuid } from 'uuid';

// Hook for any button that wants to enqueue work on a DS chat.
//
// The button shouldn't call `forge.*` directly. It calls `enqueue(...)` and
// reads `items` to determine its own queued state (`items.find(e =>
// e.clientKey === myKey)`).

type EnqueueArgs = {
	label: string;
	clientKey?: string;
	payload: DsChatActionPayload;
};

type Result = {
	items: DsChatActionEntry[];
	enqueue: (args: EnqueueArgs) => void;
	cancel: (id: string) => void;
};

export const useDsChatQueue = (chatId: string | null): Result => {
	const dispatch = useDispatch();

	const items = useSelector(
		(r: RootReducer) =>
			chatId ? r.mode.dsChatActionQueue.filter((e) => e.chatId === chatId) : [],
		shallowEqual
	);

	const enqueue = useCallback(
		(args: EnqueueArgs) => {
			if (!chatId) return;
			dispatch(
				enqueueDsChatAction({
					id: uuid(),
					chatId,
					createdAt: Date.now(),
					label: args.label,
					...(args.clientKey != null && { clientKey: args.clientKey }),
					payload: args.payload,
				})
			);
		},
		[chatId, dispatch]
	);

	const cancel = useCallback(
		(id: string) => {
			dispatch(cancelDsChatAction({ id }));
		},
		[dispatch]
	);

	return useMemo(() => ({ items, enqueue, cancel }), [items, enqueue, cancel]);
};
